import React, { useState, useEffect, useRef } from 'react';
import Stories from 'react-insta-stories';
import { X, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import './EventWebstories.css';

const CustomVideoStory = ({ src, action, isPaused }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const handleMuteChange = (e) => {
      if (videoRef.current) {
        videoRef.current.muted = e.detail.muted;
      }
    };
    window.addEventListener('webstory-mute-toggle', handleMuteChange);
    
    // Set initial state from global or default to false
    if (videoRef.current) {
      videoRef.current.muted = window.__webstoryMuted ?? false;
    }

    return () => window.removeEventListener('webstory-mute-toggle', handleMuteChange);
  }, []);

  useEffect(() => {
    if (isPaused) {
      videoRef.current?.pause();
    } else {
      videoRef.current?.play().catch(() => {});
    }
  }, [isPaused]);

  return (
    <video 
      ref={videoRef}
      src={src} 
      autoPlay 
      playsInline 
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onEnded={action.next}
    />
  );
};

export default function EventWebstories({ webstories }) {
  const [activeStoryIndex, setActiveStoryIndex] = useState(null);
  const [isMuted, setIsMuted] = useState(window.__webstoryMuted ?? false);

  const toggleMute = (e) => {
    e.stopPropagation();
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    window.__webstoryMuted = newMuted;
    window.dispatchEvent(new CustomEvent('webstory-mute-toggle', { detail: { muted: newMuted } }));
  };

  if (!webstories || webstories.length === 0) return null;

  const handleStoryClick = (index) => {
    const story = webstories[index];
    if (!story.items || story.items.length === 0) return;
    setActiveStoryIndex(index);
  };

  const closeStory = () => {
    setActiveStoryIndex(null);
  };

  const goToPrevStory = () => {
    if (activeStoryIndex > 0) {
      setActiveStoryIndex(activeStoryIndex - 1);
    }
  };

  const goToNextStory = () => {
    if (activeStoryIndex < webstories.length - 1) {
      setActiveStoryIndex(activeStoryIndex + 1);
    } else {
      closeStory();
    }
  };

  const activeStory = activeStoryIndex !== null ? webstories[activeStoryIndex] : null;
  const prevStory = activeStoryIndex !== null && activeStoryIndex > 0 ? webstories[activeStoryIndex - 1] : null;
  const nextStory = activeStoryIndex !== null && activeStoryIndex < webstories.length - 1 ? webstories[activeStoryIndex + 1] : null;

  const getStoryFirstMedia = (story) => {
    const first = story?.items?.[0];
    if (!first) return null;
    return first.type === 'video' ? (
      <video src={first.url} className="webstory-side-media" muted />
    ) : (
      <img src={first.url} alt={story.title} className="webstory-side-media" />
    );
  };

  return (
    <div className="event-detail-card event-webstories-section">
      <h2 className="event-detail-section-title">Webstories</h2>
      <div className="webstories-scroll-container">
        {webstories.map((story, index) => {
          const firstItem = story.items?.[0];
          const hasCover = !!firstItem;
          return (
            <div key={story.id} className="webstory-highlight" onClick={() => handleStoryClick(index)}>
              {hasCover && firstItem.type === 'video' ? (
                <video src={firstItem.url} muted loop autoPlay playsInline className="webstory-cover-media" />
              ) : (
                <img src={hasCover ? firstItem.url : 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=150'} alt={story.title} className="webstory-cover-media" />
              )}
              <div className="webstory-overlay">
                <span className="webstory-overlay-title">{story.title}</span>
              </div>
            </div>
          );
        })}
      </div>

      {activeStory && (
        <div className="webstory-modal-overlay">
          <button className="webstory-modal-close" onClick={closeStory}>
            <X size={32} color="white" />
          </button>
          
          <button 
            className="webstory-modal-mute" 
            onClick={toggleMute}
          >
            {isMuted ? <VolumeX size={24} color="white" /> : <Volume2 size={24} color="white" />}
          </button>

          <div className="webstory-modal-layout">
            {/* Previous Story Preview */}
            <div className="webstory-side-preview left" onClick={goToPrevStory}>
              {prevStory && (
                <>
                  <div className="webstory-side-content">
                    {getStoryFirstMedia(prevStory)}
                    <div className="webstory-side-overlay">
                      <span>{prevStory.title}</span>
                    </div>
                  </div>
                  <button className="webstory-nav-btn prev-btn"><ChevronLeft size={24} color="white" /></button>
                </>
              )}
            </div>

            {/* Active Story */}
            <div className="webstory-modal-content">
              <Stories
                key={activeStory.id} // forces remount on story change
                stories={activeStory.items.map(item => ({
                  url: item.url,
                  type: item.type === 'video' ? 'video' : 'image',
                  header: {
                    heading: activeStory.title
                  },
                  // Use a custom video component to properly listen for mute changes without re-rendering Stories
                  ...(item.type === 'video' ? {
                    content: ({ action, isPaused }) => (
                      <CustomVideoStory src={item.url} action={action} isPaused={isPaused} />
                    )
                  } : {})
                }))}
                defaultInterval={5000}
                width={360}
                height={640}
                onAllStoriesEnd={goToNextStory}
                keyboardNavigation
              />
            </div>

            {/* Next Story Preview */}
            <div className="webstory-side-preview right" onClick={goToNextStory}>
              {nextStory && (
                <>
                  <div className="webstory-side-content">
                    {getStoryFirstMedia(nextStory)}
                    <div className="webstory-side-overlay">
                      <span>{nextStory.title}</span>
                    </div>
                  </div>
                  <button className="webstory-nav-btn next-btn"><ChevronRight size={24} color="white" /></button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
